
import { take, mergeMap, map, filter, debounceTime, switchMap, takeUntil, mapTo } from 'rxjs/operators'
import { combineEpics } from 'redux-observable'
import { Epic } from 'interfaces/middleware'
import { FORM_STOP_ASYNC_VALIDATE, FormStopAsyncValidate, SUBMIT_FORM } from 'actions/formActions'
import { setUnexpectedErrorActionCreator } from 'actions/globalActions'
import { setShownSidebarPaneActionCreator } from 'actions/sidebarActions'
import { isMobileSelector } from 'selectors/viewportSelectors'
import {
  TOGGLE_AUTHOR_FOLLOWING,
  toggleAuthorFollowingActionCreator,
  TOGGLE_POST_FOLLOWING,
  togglePostFollowingActionCreator,
  TOGGLE_COMMENT_LIKE,
  toggleCommentLikeActionCreator,
  TOGGLE_POST_LIKE,
  togglePostLikeActionCreator,
  REPORT_COMMENT,
  reportCommentActionCreator,
  REPORT_POST,
  reportPostActionCreator,
  DELETE_POST,
  deletePostActionCreator,
  DELETE_COMMENT,
  deleteCommentActionCreator,
  GET_POSTS,
  getPostsActionCreator,
  GET_FEED_MEMBER_INFO,
  getFeedMemberInfoActionCreator,
  GET_FEED_AUTHOR,
  getFeedAuthorActionCreator,
  GET_FEED_TAG,
  getFeedTagActionCreator,
  loadFollowedPostsCountActionCreator,
  LOAD_FOLLOWED_POSTS_COUNT,
  GET_FILTERS_STATUS,
  getFiltersStatusActionCreator,
  GET_COMMENTS,
  getCommentsActionCreator,
  changeSocialProfileStepActionCreator,
  GET_FOLLOWED_USERS,
  getFollowedUsersActionCreator,
  GET_FOLLOWED_TAGS,
  getFollowedTagsActionCreator,
  TOGGLE_TAG_FOLLOWING,
  toggleTagFollowingActionCreator,
  GET_RANDOM_FEED_RELATION,
  getRandomFeedRelationActionCreator,
  RANDOMIZE_FEED_RELATION,
  randomizeFeedRelationActionCreator,
  resetFollowedUsersActionCreator,
  scheduleResetFollowedUsersActionCreator,
  SELECT_FEED_FILTER,
  resetFollowedTagsActionCreator,
  scheduleResetFollowedTagsActionCreator,
  resetPostsActionCreator,
  LOAD_COMMENT_THREAD,
  loadCommentThreadActionCreator,
  REPORT_INSIGHT,
  reportInsightActionCreator,
  GET_FEED_TAGS,
  getFeedTagsActionCreator,
  CLICK_COMPOSE,
} from 'actions/feedActions'
import { get, post, deleteAjax, urlSearchParamsToGetQueryParams } from 'helpers/observableAjaxHelpers'
import { Observable, merge, of, empty, from } from 'rxjs'
import { Store, State } from 'interfaces/state'
import {
  FOLLOW_USER_URL,
  UNFOLLOW_USER_URL,
  FOLLOW_POST_URL,
  UNFOLLOW_POST_URL,
  LIKE_COMMENT_URL,
  UNLIKE_COMMENT_URL,
  LIKE_POST_URL,
  UNLIKE_POST_URL,
  REPORT_COMMENT_URL,
  GET_POSTS_URL,
  GET_POSTS_PUBLIC_URL,
  GET_POST_URL,
  GET_POST_PUBLIC_URL,
  REPORT_POST_URL,
  DELETE_POST_URL,
  DELETE_COMMENT_URL,
  GET_FEED_MEMBER_INFO_URL,
  GET_FOLLOWED_POSTS_COUNT_URL,
  GET_FILTERS_STATUS_URL,
  GET_COMMENTS_URL,
  GET_COMMENTS_PUBLIC_URL,
  GET_FEED_USERNAME_EXISTS_URL,
  GET_FOLLOWED_USERS_URL,
  GET_FOLLOWED_TAGS_URL,
  FOLLOW_TAG_URL,
  UNFOLLOW_TAG_URL,
  GET_FEED_AUTHOR_URL,
  GET_FEED_AUTHOR_PUBLIC_URL,
  GET_FEED_TAG_URL,
  GET_FEED_TAG_PUBLIC_URL,
  GET_RANDOM_FEED_PEER_GROUP_URL,
  RANDOMIZE_FEED_PEER_GROUP_URL,
  REPORT_AD_POST_URL,
  GET_TAGS_URL,
} from 'interfaces/ajax'
import {
  GetPostsApiPayload,
  RawPost,
  GetCommentsApiPayload,
  FeedUser,
  GetFollowedUsersApiPayload,
  GetFollowedTagsApiPayload,
  FeedOrdering,
  SocialProfileFormValues,
  SelectableFeedFilterPeergroup,
  SelectableFeedFilterFeedgroup,
  TagCollection,
} from 'interfaces/feed'
import { normalize } from 'helpers/normalizeHelpers'
import { CursorPaginatable, RawCursorPaginatable } from 'interfaces/table'
import { FEED_OWN_POST_COMPOSE_ID } from 'constants/feed'
import { UNAUTHORIZED_STATUS_CODE, NOT_FOUND_STATUS_CODE, VALIDATION_ERROR_STATUS_CODE } from 'constants/ajax'
import { processError, getResponseFormErrors } from 'helpers/ajaxHelpers'
import {
  feedSelectedFilterSelector,
  createFeedAllCommentUidsByPostUidSelector,
  feedDataSelector,
  feedAppSelector,
  filterRandomRelationStateSelector,
  memberFeedInfoSelector,
  createCommentThreadSelector,
  createFeedCommentSelector,
} from 'selectors/feedSelectors'
import { activateAnchorActionCreator } from 'actions/navigationActions'
import { isFeedPostsPage, normalizeComments, normalizePosts, normalizeImage, getFeedCommentAnchorId } from 'helpers/feedHelpers'
import { OrderingApiPayload, PageSizeApiPayload, CursorBasedApiPayload } from 'interfaces/ajax/payload'
import { POSTS_PER_PAGE, COMMENTS_PER_PAGE, FOLLOWED_USERS_PER_PAGE, FOLLOWED_TAGS_PER_PAGE } from 'constants/api'
import { Action } from 'actions'
import { FORM_SOCIAL_PROFILE } from 'constants/forms'
import { getFormAsyncErrors, getFormValues, stopSubmit } from 'redux-form'
import { FEED_USERNAME_EXISTS } from 'constants/errors'
import { FormErrorsResponse } from 'interfaces/component'
import { extractRelation } from 'helpers/peergroupsHelpers'
import { isBasePaginationPristine } from 'helpers/tableHelpers'
import { peersRelationSelector } from 'selectors/peergroupSelectors'
import { viewportSegmentSelector } from 'selectors/viewportSelectors'
import { UserImage } from 'interfaces/userImage'
import { feedComposeSelectFilterActionCreator, composeExecCommandActionCreator, COMPOSE_EXISTS } from 'actions/feedComposeActions'
import { isAuthenticatedSelector } from 'selectors/globalSelectors'
import { pick, encodeUrlParams } from 'helpers/utilsHelpers'
import { Omit } from 'interfaces'
import { isComposeExistsSelector } from 'selectors/feedComposeSelectors'
import { byType } from 'rxOperators/byType'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { ajaxCatch } from 'rxOperators/ajaxCatch'
import { formSubmitted } from 'rxOperators/formsLegacy'
import { catchAjaxSafe } from 'rxOperators/catchSafe'

const getFeedMemberEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(GET_FEED_MEMBER_INFO, 'request')).pipe(
      mergeMap((action) =>
        get(GET_FEED_MEMBER_INFO_URL).pipe(
          map(({ response }) => {
            const { avatar, ...restMember } = response
            return getFeedMemberInfoActionCreator.success(action.payload.params, {
              member: {
                ...restMember,
                ...avatar ? { avatarId: avatar.id } : { avatarId: null },
              },
              avatar: avatar ? normalizeImage(avatar) : null,
            })
          }))
          .pipe(catchAjaxSafe((error) => {
            if (error.status === UNAUTHORIZED_STATUS_CODE) {
              return of(getFeedMemberInfoActionCreator.fail(action.payload.params, error.status))
            }

            return processError(error)
          }))
      ))

const getAuthorEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(filterRequestOfType(GET_FEED_AUTHOR, 'request')).pipe(
      mergeMap((action) => {
        const state = store.getState()
        const isAuthenticated = isAuthenticatedSelector(state)

        const request$ = isAuthenticated
          ? get(GET_FEED_AUTHOR_URL, action.payload.params)
          : get(GET_FEED_AUTHOR_PUBLIC_URL, action.payload.params)

        return request$.pipe(
          map(({ response }) => getFeedAuthorActionCreator.success(action.payload.params, response)))
          .pipe(catchAjaxSafe((error) => {
            if (error.status === UNAUTHORIZED_STATUS_CODE) {
              return of(getFeedAuthorActionCreator.fail(action.payload.params, error.status))
            }

            if (error.status === NOT_FOUND_STATUS_CODE) {
              return of(setUnexpectedErrorActionCreator('user-not-found', error))
            }

            return processError(error)
          }))
      }))

const getTagEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(filterRequestOfType(GET_FEED_TAG, 'request')).pipe(
      mergeMap((action) => {
        const state = store.getState()
        const isAuthenticated = isAuthenticatedSelector(state)

        const request$ = isAuthenticated
          ? get({ url: GET_FEED_TAG_URL, params: { name: action.payload.params.tagName }})
          : get({ url: GET_FEED_TAG_PUBLIC_URL, params: { name: action.payload.params.tagName }})

        return request$.pipe(
          map(({ response }) => getFeedTagActionCreator.success(action.payload.params, response)))
          .pipe(catchAjaxSafe((error) => {
            if (error.status === UNAUTHORIZED_STATUS_CODE) {
              return of(getFeedTagActionCreator.fail(action.payload.params, error.status))
            }

            if (error.status === NOT_FOUND_STATUS_CODE) {
              return of(setUnexpectedErrorActionCreator('tag-not-found', error))
            }

            return processError(error)
          }))
      }))

const toggleUserFollowingEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(TOGGLE_AUTHOR_FOLLOWING, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { uid, toFollow } = params

        const request$ = toFollow
          ? post({ url: FOLLOW_USER_URL, params: { uid }})
          : post({ url: UNFOLLOW_USER_URL, params: { uid }})

        return request$.pipe(
          map(({ response }) =>
            toggleAuthorFollowingActionCreator.success(params, response)
          ))
          .pipe(catchAjaxSafe((error) =>
            of(toggleAuthorFollowingActionCreator.fail(params, error.status))
          ))
      }))

const togglePostFollowingEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(TOGGLE_POST_FOLLOWING, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { postUid, toFollow } = params

        const request$ = toFollow
          ? post({ url: FOLLOW_POST_URL, params: { uid: postUid }})
          : post({ url: UNFOLLOW_POST_URL, params: { uid: postUid }})

        return request$.pipe(
          map(({ response }) =>
            togglePostFollowingActionCreator.success(params, response)
          ))
          .pipe(catchAjaxSafe((error) =>
            of(togglePostFollowingActionCreator.fail(params, error.status))
          ))
      }))

const toggleTagFollowingEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(TOGGLE_TAG_FOLLOWING, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { tag_name, toFollow } = params

        const request$ = toFollow
          ? post({ url: FOLLOW_TAG_URL, params: { tag_name }})
          : post({ url: UNFOLLOW_TAG_URL, params: { tag_name }})

        return request$.pipe(
          map(({ response }) =>
            toggleTagFollowingActionCreator.success(params, response)
          ))
          .pipe(catchAjaxSafe((error) =>
            of(toggleTagFollowingActionCreator.fail(params, error.status))
          ))
      }))

const toggleCommentLikeEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(TOGGLE_COMMENT_LIKE, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { commentUid: id, toLike } = params

        const request$ = toLike
          ? post({ url: LIKE_COMMENT_URL, params: { id }})
          : post({ url: UNLIKE_COMMENT_URL, params: { id }})

        return request$.pipe(
          map(({ response }) =>
            toggleCommentLikeActionCreator.success(params, response)
          ))
          .pipe(catchAjaxSafe((error) =>
            of(toggleCommentLikeActionCreator.fail(params, error.status))
          ))
      }))

const togglePostLikeEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(TOGGLE_POST_LIKE, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { postUid, toLike } = params

        const request$ = toLike
          ? post({ url: LIKE_POST_URL, params: { uid: postUid }})
          : post({ url: UNLIKE_POST_URL, params: { uid: postUid }})

        return request$.pipe(
          map(({ response }) =>
            togglePostLikeActionCreator.success(params, response)
          ))
          .pipe(catchAjaxSafe((error) =>
            of(togglePostLikeActionCreator.fail(params, error.status))
          ))
      }))

const reportCommentEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(REPORT_COMMENT, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { uid: id } = params

        return post({ url: REPORT_COMMENT_URL, params: { id }}).pipe(
          map(({ response }) =>
            reportCommentActionCreator.success(params, response)
          ))
          .pipe(catchAjaxSafe((error) =>
            of(reportCommentActionCreator.fail(params, error.status))
          ))
      }))

type GetCursorPaginatablePayloadParams = {
  prevPaginatable: CursorPaginatable<any> | null
  ordering?: FeedOrdering
  pageSize: number
}
type GetCursoredItemsApiPayload =
  | (
    & OrderingApiPayload
    & PageSizeApiPayload
  )
  | (
    CursorBasedApiPayload
  )

const generateGetCursoredItemsPayload = (params: GetCursorPaginatablePayloadParams): GetCursoredItemsApiPayload | null => {
  const {
    prevPaginatable,
    ordering,
    pageSize,
  } = params

  // coming `ordering` means we need to drop existing cursor
  if (ordering) {
    return {
      ordering,
      page_size: pageSize,
    }
  }

  if (prevPaginatable && prevPaginatable.pagination && prevPaginatable.pagination.next) {
    return urlSearchParamsToGetQueryParams(new URL(prevPaginatable.pagination.next).searchParams) as GetCursoredItemsApiPayload
  }

  return null
}

const postThreadSelector = createFeedAllCommentUidsByPostUidSelector()
const getCommentsEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(filterRequestOfType(GET_COMMENTS, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { postUid, ordering, highlightCommentUid } = params

        const postUidObj = { postUid }

        const state = store.getState()
        const thread = postThreadSelector(state, postUidObj)

        const isAuthenticated = isAuthenticatedSelector(state)

        const partialApiPayload = generateGetCursoredItemsPayload({
          prevPaginatable: thread,
          ordering,
          pageSize: COMMENTS_PER_PAGE,
        })

        if (!partialApiPayload) {
          return empty()
        }

        const apiPayload: GetCommentsApiPayload = {
          ...partialApiPayload,
          post_uid: postUid,
        }

        const request$ = isAuthenticated
          ? get(GET_COMMENTS_URL, apiPayload)
          : get(GET_COMMENTS_PUBLIC_URL, apiPayload)

        return request$.pipe(
          mergeMap(({ response }) => {
            const responseActions: Action[] = [
              getCommentsActionCreator.success(params, normalizeComments(response, postUid)),
            ]
            if (highlightCommentUid) {
              responseActions.push(
                activateAnchorActionCreator(getFeedCommentAnchorId(highlightCommentUid))
              )
            }

            return from(responseActions)
          }))
          .pipe(catchAjaxSafe((error) =>
            of(getCommentsActionCreator.fail(params, error.status))
          ))
      }))

const commentThreadSelector = createCommentThreadSelector()
const loadThreadEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(filterRequestOfType(LOAD_COMMENT_THREAD, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { commentUid, postUid } = params
        const state = store.getState()
        const thread = commentThreadSelector(state, { commentUid })
        if (!thread || !thread.pagination || !thread.pagination.next) {
          return empty()
        }

        const apiPayload: GetCommentsApiPayload = urlSearchParamsToGetQueryParams(new URL(thread.pagination.next).searchParams) as GetCommentsApiPayload

        const isAuthenticated = isAuthenticatedSelector(state)
        const request$ = isAuthenticated
          ? get(GET_COMMENTS_URL, apiPayload)
          : get(GET_COMMENTS_PUBLIC_URL, apiPayload)

        return request$.pipe(
          map(({ response }) => loadCommentThreadActionCreator.success(params, normalizeComments(response, postUid))))
          .pipe(catchAjaxSafe((error) =>
            of(loadCommentThreadActionCreator.fail(params, error.status))
          ))
      }))

const getPostsEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(filterRequestOfType(GET_POSTS, 'request')).pipe(
      filter((action) => action.payload.params.query.filter_by !== 'post'),
      mergeMap((action) => {
        const { params } = action.payload
        const state = store.getState()
        const isAuthenticated = isAuthenticatedSelector(state)

        const partialApiPayload = generateGetCursoredItemsPayload({
          prevPaginatable: params.feedPostUids,
          ordering: params.ordering,
          pageSize: POSTS_PER_PAGE,
        })

        const apiPayload: GetPostsApiPayload = {
          ...partialApiPayload,
          ...params.query,
        } as GetPostsApiPayload

        const request$ = isAuthenticated
          ? get(GET_POSTS_URL, apiPayload)
          : get(GET_POSTS_PUBLIC_URL, apiPayload)

        return request$.pipe(
          map(({ response }) => getPostsActionCreator.success(params, normalizePosts(response))))
          .pipe(ajaxCatch((error) =>
            of(getPostsActionCreator.fail(params, error.status))
          ))
      }))

const getPostEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(filterRequestOfType(GET_POSTS, 'request')).pipe(
      filter((action) => action.payload.params.query.filter_by === 'post'),
      mergeMap((action) => {
        const { params } = action.payload
        const { query } = params
        if (query.filter_by !== 'post') {
          return empty()
        }

        const state = store.getState()
        const isAuthenticated = isAuthenticatedSelector(state)

        const request$ = isAuthenticated
          ? get({ url: GET_POST_URL, params: query })
          : get({ url: GET_POST_PUBLIC_URL, params: query })

        return request$.pipe(
          map(({ response }) => {

            const fakePaginatable: RawCursorPaginatable<RawPost> = {
              previous: null,
              next: null,
              results: [response],
            }

            return getPostsActionCreator.success(params, normalizePosts(fakePaginatable))
          }))
          .pipe(catchAjaxSafe((error) => {
            if (error.status === NOT_FOUND_STATUS_CODE) {
              return of(setUnexpectedErrorActionCreator('post-not-found', error))
            }

            return of(getPostsActionCreator.fail(params, error.status))
          }))
      }))

const reportPostEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(REPORT_POST, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { postUid } = params

        return post({ url: REPORT_POST_URL, params: { uid: postUid }}).pipe(
          map(({ response }) =>
            reportPostActionCreator.success(params, response)
          ))
          .pipe(catchAjaxSafe((error) =>
            of(reportPostActionCreator.fail(params, error.status))
          ))
      }))

const reportAdPostEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(REPORT_INSIGHT, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { postUid, insightId } = params

        return post({ url: REPORT_AD_POST_URL, params: { uid: postUid }}, { body: { insight_id: insightId }}).pipe(
          map(({ response }) =>
            reportInsightActionCreator.success(params, response)
          ))
          .pipe(catchAjaxSafe((error) =>
            of(reportInsightActionCreator.fail(params, error.status))
          ))
      }))

const deletePostEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(DELETE_POST, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { postUid } = params

        return deleteAjax({ url: DELETE_POST_URL, params: { uid: postUid }}).pipe(
          map(({ response }) =>
            deletePostActionCreator.success(params, response)
          ))
          .pipe(catchAjaxSafe((error) =>
            of(deletePostActionCreator.fail(params, error.status))
          ))
      }))

const commentSelector = createFeedCommentSelector()
const deleteCommentEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(filterRequestOfType(DELETE_COMMENT, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { uid: commentUid } = params

        return deleteAjax({ url: DELETE_COMMENT_URL, params: { id: commentUid }}).pipe(
          map(() => {
            const state = store.getState()
            const comment = commentSelector(state, { commentUid })
            return deleteCommentActionCreator.success(params, {
              childrenCount: comment ? comment.childrenCount : 0,
            })
          }))
          .pipe(catchAjaxSafe((error) =>
            of(deleteCommentActionCreator.fail(params, error.status))
          ))
      }))

const loadFollowedPostsCountEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(LOAD_FOLLOWED_POSTS_COUNT, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload

        return get(GET_FOLLOWED_POSTS_COUNT_URL).pipe(
          map(({ response }) =>
            loadFollowedPostsCountActionCreator.success(params, response)
          ))
          .pipe(ajaxCatch((error) =>
            of(loadFollowedPostsCountActionCreator.fail(params, error.status))
          ))
      }))

const getFiltersStatusEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(GET_FILTERS_STATUS, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload

        return post(GET_FILTERS_STATUS_URL, {
          body: { items: params },
        }).pipe(
          map(({ response }) =>
            getFiltersStatusActionCreator.success(params, response.items)
          ))
          .pipe(ajaxCatch((error) =>
            of(getFiltersStatusActionCreator.fail(params, error.status))
          ))
      }))

const checkUsernameExistsEpic: Epic = (action$, store: Store) => {
  const FIELD_NAME = 'username' as const

  const stopAsyncValidate$: Observable<FormStopAsyncValidate> =
    action$
      .pipe(byType(FORM_STOP_ASYNC_VALIDATE)).pipe(
        filter((action) => {
          const { form } = action.meta

          return form === FORM_SOCIAL_PROFILE
        }),
        filter((action) => {
          const { form } = action.meta

          const state = store.getState()
          const asyncErrors = getFormAsyncErrors(form)(state)

          return !asyncErrors || !(FIELD_NAME in asyncErrors)
        }))


  return stopAsyncValidate$.pipe(
    debounceTime(200),
    switchMap((action) => {
      const state = store.getState()
      const { form } = action.meta
      const formValuesSelector = getFormValues<Pick<SocialProfileFormValues, 'username'>, State>(form)
      const asyncErrorsSelector = getFormAsyncErrors(form)

      const formValues = formValuesSelector(state)
      if (!formValues) {
        return empty()
      }

      const enteredUsername = formValues[FIELD_NAME]
      const feedUser = memberFeedInfoSelector(state)
      if (
        feedUser
          && typeof enteredUsername === 'string'
          && typeof feedUser.username === 'string'
          && feedUser.username.toLowerCase() === enteredUsername.toLowerCase()
      ) {
        const prevAsyncErrors = asyncErrorsSelector(state)
        return of(stopSubmit(form, prevAsyncErrors))
      }

      const submitForm$ =
        action$.pipe(
          filter((a) => a.type === SUBMIT_FORM && a.payload.formName === form))

      const username = formValues[FIELD_NAME]
      if (!username) {
        return empty()
      }

      return get(GET_FEED_USERNAME_EXISTS_URL, { username }).pipe(
        takeUntil(submitForm$),
        map(({ response }) => {
          const { exists } = response
          const prevAsyncErrors = asyncErrorsSelector(store.getState())

          if (!exists) {
            return stopSubmit(form, {
              ...prevAsyncErrors,
            })
          }

          return stopSubmit(form, {
            ...prevAsyncErrors,
            [FIELD_NAME]: FEED_USERNAME_EXISTS,
          })
        }))
        .pipe(catchAjaxSafe((error) => {
          const { status, xhr: { response }} = error

          if (status === VALIDATION_ERROR_STATUS_CODE) {
            const responseErrors: FormErrorsResponse = typeof response === 'string' ? JSON.parse(response) : response
            const prevAsyncErrors = asyncErrorsSelector(store.getState())

            const nextAsyncErrors = {
              ...prevAsyncErrors,
              ...getResponseFormErrors(responseErrors || {}),
            }

            return of(stopSubmit(form, nextAsyncErrors))
          }

          return processError(error)
        }))
    }))
}

const socialProfileCreatedEpic: Epic = (action$) =>
  action$
    .pipe(formSubmitted(FORM_SOCIAL_PROFILE)).pipe(
      mapTo(changeSocialProfileStepActionCreator('confirmation')))

type FollowedUsersAccItem = {
  users: FeedUser[]
  avatars: UserImage[]
}
const getFollowedUsersEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(GET_FOLLOWED_USERS, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { links } = params

        const cursor: string | null = links && links.next
          ? new URL(links.next).searchParams.get('cursor')
          : null
        const query: GetFollowedUsersApiPayload = cursor
          ? { page_size: FOLLOWED_USERS_PER_PAGE, cursor }
          : { page_size: FOLLOWED_USERS_PER_PAGE }

        return get(GET_FOLLOWED_USERS_URL, query).pipe(
          map(({ response }) => {
            const { users, avatars } = response.results.reduce<FollowedUsersAccItem>((acc, item) => {
              const sharedKeys: (keyof Omit<FeedUser, 'avatarId'>)[] = [
                'uid',
                'username',
                'badges',
                'is_followed',
                'posts_count',
                'followers_count',
                'likes_count',
                'peergroup',
              ]

              const nextUser: FeedUser = {
                ...pick(item, sharedKeys),
                avatarId: item.avatar ? item.avatar.id : null,
              }

              acc.users.push(nextUser)

              if (item.avatar) {
                acc.avatars.push(normalizeImage(item.avatar))
              }

              return acc
            }, {
              users: [],
              avatars: [],
            })

            return getFollowedUsersActionCreator.success(params, {
              users: normalize(users, 'uid'),
              avatars: normalize(avatars, 'id'),
              links: {
                next: response.next,
                previous: response.previous,
              },
            })
          }))
          .pipe(ajaxCatch((error) => {
            return of(getFollowedUsersActionCreator.fail(params, error.status))
          }))
      }))

const resetFollowedUsersEpic: Epic = (action$, store) =>
  action$
    .pipe(filterRequestOfType(TOGGLE_AUTHOR_FOLLOWING, 'success')).pipe(
      mergeMap(() => {
        const state = store.getState()
        const followedPeopleLoading = feedDataSelector(state).followedUsersLoading
        const feedApp = feedAppSelector(state)
        const { shouldResetFollowedPeople } = feedApp

        if (!shouldResetFollowedPeople && !isBasePaginationPristine(followedPeopleLoading)) {
          const shownPopup = feedApp.filterPopup.shownFilter
          const isPeoplePopupShown = shownPopup
          && shownPopup.type === 'feedgroup'
          && shownPopup.slug === 'followed_people'

          return of(
            isPeoplePopupShown
              ? scheduleResetFollowedUsersActionCreator()
              : resetFollowedUsersActionCreator()
          )
        }

        return empty()
      }))

const getFollowedTagsEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(GET_FOLLOWED_TAGS, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload
        const { links } = params

        const cursor: string | null = links && links.next
          ? new URL(links.next).searchParams.get('cursor')
          : null
        const query: GetFollowedTagsApiPayload = cursor
          ? { page_size: FOLLOWED_TAGS_PER_PAGE, cursor }
          : { page_size: FOLLOWED_TAGS_PER_PAGE }

        return get(GET_FOLLOWED_TAGS_URL, query).pipe(
          map(({ response }) =>
            getFollowedTagsActionCreator.success(params, {
              tags: normalize(response.results, 'name'),
              links: {
                next: response.next,
                previous: response.previous,
              },
            })
          ))
          .pipe(ajaxCatch((error) =>
            of(getFollowedTagsActionCreator.fail(params, error.status))
          ))
      }))

const resetFollowedTagsEpic: Epic = (action$, store) =>
  action$
    .pipe(filterRequestOfType(TOGGLE_TAG_FOLLOWING, 'success')).pipe(
      mergeMap(() => {
        const state = store.getState()
        const followedTagsLoading = feedDataSelector(state).followedTagsLoading
        const feedApp = feedAppSelector(state)
        const { shouldResetFollowedTags } = feedApp

        if (!shouldResetFollowedTags && !isBasePaginationPristine(followedTagsLoading)) {
          const shownPopup = feedAppSelector(state).filterPopup.shownFilter
          const isTagsPopupShown = shownPopup
          && shownPopup.type === 'feedgroup'
          && shownPopup.slug === 'followed_tags'

          return of(
            isTagsPopupShown
              ? scheduleResetFollowedTagsActionCreator()
              : resetFollowedTagsActionCreator()
          )
        }

        return empty()
      }))

const getRandomFeedRelationEpic: Epic = (action$) =>
  action$
    .pipe(filterRequestOfType(GET_RANDOM_FEED_RELATION, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload

        return get(GET_RANDOM_FEED_PEER_GROUP_URL).pipe(
          map(({ response }) =>
            getRandomFeedRelationActionCreator.success(params, {
              relation: extractRelation(response[0]),
              peergroup: response[0].peergroup,
            })
          ))
          .pipe(ajaxCatch((error) =>
            of(getRandomFeedRelationActionCreator.fail(params, error.status))
          ))
      }))

const randomizeFeedRelationEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(filterRequestOfType(RANDOMIZE_FEED_RELATION, 'request')).pipe(
      mergeMap((action) => {
        const { params } = action.payload

        return post(RANDOMIZE_FEED_PEER_GROUP_URL).pipe(
          mergeMap(({ response }) => {
            const state = store.getState()
            const selectedFilter = feedSelectedFilterSelector(state)

            const actions: Action[] = [
              randomizeFeedRelationActionCreator.success(params, {
                relation: extractRelation(response),
                peergroup: response.peergroup,
              }),
            ]

            if (selectedFilter && selectedFilter.type === 'peergroup' && selectedFilter.relationId === response.id) {
            // we could reset only random peergroup posts, but it doesn't matter because now we drop a group's cache when the group is selected
              actions.unshift(resetPostsActionCreator())
            }

            return from(actions)
          }))
          .pipe(ajaxCatch((error) =>
            of(randomizeFeedRelationActionCreator.fail(params, error.status))
          ))
      }))

const selectFeedFilterEpic: Epic = (action$, store: Store) =>
  action$
    .pipe(byType(SELECT_FEED_FILTER)).pipe(
      filter(isFeedPostsPage),
      mergeMap((action) => {
        let filterLocal = { ...action.payload }
        const state = store.getState()
        const everyoneGroup = { type: 'feedgroup', slug: 'everyone' } as SelectableFeedFilterFeedgroup

        switch (action.payload.type) {
          case 'your-posts':
          case 'feedgroup':
            const yourRelation = peersRelationSelector(state)
            if (action.payload.type === 'feedgroup' && action.payload.slug === 'everyone') {
              filterLocal = everyoneGroup
            } else if (yourRelation) {
              filterLocal = {
                type: 'peergroup',
                relationId: yourRelation.id,
              } as SelectableFeedFilterPeergroup
            }

            break

          case 'peergroup':
            const randomState = filterRandomRelationStateSelector(state).data
            const randomIsSelected = randomState && randomState.relation.id === action.payload.relationId
            if (randomIsSelected) {
              filterLocal = everyoneGroup
            }

            break

          default:
            break
        }

        const isMobile = isMobileSelector(state)
        const setShownPane$ = isMobile
          ? of(setShownSidebarPaneActionCreator(null))
          : empty()

        return merge(
          setShownPane$,
          of(feedComposeSelectFilterActionCreator({ filter: filterLocal, composeId: FEED_OWN_POST_COMPOSE_ID })),
          of(activateAnchorActionCreator('feedposts_compose', {
            behavior: 'smooth',
            block: 'start',
            preventScrollWhenVisible: viewportSegmentSelector(state) === 'desktop',
          }))
        )
      }))

const getFeedTagsBySearch: Epic = (action$, store) =>
  action$
    .pipe(filterRequestOfType(GET_FEED_TAGS, 'request')).pipe(
      mergeMap((action) => {
        const keyObj = pick(action.payload.params, ['search', 'ordering', 'limit'])
        const { search, ordering, limit } = keyObj

        const key = encodeUrlParams(keyObj)
        const { tagsBySearch } = feedDataSelector(store.getState())
        const currentSearch = tagsBySearch[key]
        if (currentSearch && currentSearch.payload) {
          return empty()
        }

        const apiPayload = {
          ...(ordering ? { ordering } : null),
          ...(search ? { search: encodeURIComponent(search) } : null),
          limit,
        }

        return get(GET_TAGS_URL, apiPayload).pipe(
          map(({ response }) => {
            const itemCollection: TagCollection = normalize(response.items, 'name')
            return getFeedTagsActionCreator.success(action.payload.params, { payload: itemCollection })
          }))
          .pipe(catchAjaxSafe((error) => of(getFeedTagsActionCreator.fail(action.payload.params, error.status))))
      }))

const clickComposeEpic: Epic = (action$, store) => {
  const mountComposeIdSource = action$
    .pipe(byType(COMPOSE_EXISTS)).pipe(
      map((action) => action.payload.composeId))

  return action$
    .pipe(byType(CLICK_COMPOSE)).pipe(
      switchMap((action) => {
        const { payload } = action
        const state = store.getState()
        const isExists = isComposeExistsSelector(state, payload)

        const clickAction = composeExecCommandActionCreator({
          composeId: payload.composeId,
          command: {
            type: 'click',
          },
        })

        if (!isExists) {
          return merge(
            of(activateAnchorActionCreator('feedposts_compose')),
            mountComposeIdSource.pipe(
              filter((composeId) => composeId === payload.composeId),
              take(1),
              mapTo(clickAction))
          )
        }

        return of(clickAction)
      }))
}


export default combineEpics(
  getFeedMemberEpic,
  toggleUserFollowingEpic,
  togglePostFollowingEpic,
  toggleTagFollowingEpic,
  toggleCommentLikeEpic,
  togglePostLikeEpic,
  reportCommentEpic,
  getAuthorEpic,
  getTagEpic,
  getPostsEpic,
  getPostEpic,
  getCommentsEpic,
  loadThreadEpic,
  reportPostEpic,
  reportAdPostEpic,
  deletePostEpic,
  deleteCommentEpic,
  loadFollowedPostsCountEpic,
  getFiltersStatusEpic,
  checkUsernameExistsEpic,
  socialProfileCreatedEpic,
  getFollowedUsersEpic,
  getFollowedTagsEpic,
  getRandomFeedRelationEpic,
  randomizeFeedRelationEpic,
  resetFollowedUsersEpic,
  selectFeedFilterEpic,
  resetFollowedTagsEpic,
  getFeedTagsBySearch,
  clickComposeEpic,
)
